import { LanguageKeys } from 'src/common/constants/languages';
import { isPublished } from 'src/helpers/publishedFilter';
import { sortDistanceIds } from 'src/helpers/statisticExtras';
import { resolveResourceId } from 'src/helpers/trackMapCompetitionIcons';
import { ICompetition } from 'src/interfaces/Competition';
import { IEdition } from 'src/interfaces/Edition';
import {
  SignupBreakdownChartSeries,
  SignupBreakdownGroupBy,
  SignupBreakdownItem,
} from 'src/interfaces/SignupBreakdown';
import { TechnicalType } from 'src/interfaces/TechnicalType';

function labelFor(
  entity: Pick<TechnicalType, 'i18n' | 'name'> | undefined,
  lang: LanguageKeys,
  fallbackId: string,
): string {
  if (!entity) return fallbackId;
  return entity.i18n?.[lang] || entity.i18n?.en || entity.name || fallbackId;
}

/**
 * Builds a stacked series for the signup breakdown chart.
 * - groupBy competition: competitions on X, distances as stacks
 * - groupBy distance: distances on X, competitions as stacks
 * Zero-fills every competition×distance pair offered by the edition.
 */
export function buildSignupBreakdownSeries(
  items: readonly SignupBreakdownItem[],
  edition: Pick<IEdition, 'competitions'> | null | undefined,
  options: {
    competitionsById?: Record<string, ICompetition>;
    distancesById?: Record<string, TechnicalType>;
    lang: LanguageKeys;
    groupBy?: SignupBreakdownGroupBy;
    includeUnpublished?: boolean;
  },
): SignupBreakdownChartSeries {
  const {
    competitionsById,
    distancesById,
    lang,
    groupBy = 'competition',
    includeUnpublished = false,
  } = options;
  const empty: SignupBreakdownChartSeries = {
    labels: [],
    categoryIds: [],
    datasets: [],
    totalCount: 0,
  };

  if (!edition?.competitions?.length || !competitionsById) {
    return empty;
  }

  const competitionIds: string[] = [];
  const competitionLabels: string[] = [];
  const distanceIdSet = new Set<string>();

  for (const entry of edition.competitions) {
    const competitionId = resolveResourceId(entry.competitionId);
    const competition = competitionsById[competitionId];
    if (!competition || (!includeUnpublished && !isPublished(competition))) continue;

    competitionIds.push(competitionId);
    competitionLabels.push(labelFor(competition, lang, competitionId));

    for (const rawDistanceId of entry.distances ?? []) {
      const distanceId = resolveResourceId(rawDistanceId);
      if (!distanceId) continue;
      const distance = distancesById?.[distanceId];
      if (distance && !includeUnpublished && !isPublished(distance)) continue;
      distanceIdSet.add(distanceId);
    }
  }

  if (competitionIds.length === 0) {
    return empty;
  }

  const distanceIds = sortDistanceIds([...distanceIdSet], distancesById);

  const counts = new Map<string, number>();
  let totalCount = 0;
  for (const item of items) {
    const competitionId = resolveResourceId(item.competitionId);
    const distanceId = resolveResourceId(item.distanceId);
    const count = Number(item.count);
    if (!Number.isFinite(count) || count < 0) continue;
    totalCount += count;
    const key = `${competitionId}:${distanceId}`;
    counts.set(key, (counts.get(key) ?? 0) + count);
  }

  if (groupBy === 'distance') {
    const labels = distanceIds.map(distanceId =>
      labelFor(distancesById?.[distanceId], lang, distanceId),
    );
    const datasets = competitionIds.map((competitionId, index) => ({
      id: competitionId,
      label: competitionLabels[index] ?? competitionId,
      data: distanceIds.map(
        distanceId => counts.get(`${competitionId}:${distanceId}`) ?? 0,
      ),
    }));
    return { labels, categoryIds: distanceIds, datasets, totalCount };
  }

  const datasets = distanceIds.map(distanceId => ({
    id: distanceId,
    label: labelFor(distancesById?.[distanceId], lang, distanceId),
    data: competitionIds.map(
      competitionId => counts.get(`${competitionId}:${distanceId}`) ?? 0,
    ),
  }));

  return {
    labels: competitionLabels,
    categoryIds: competitionIds,
    datasets,
    totalCount,
  };
}

/** Sums stacked series values into one total per category (for pie view). */
export function categoryTotalsFromSeries(
  series: SignupBreakdownChartSeries,
): number[] {
  return series.labels.map((_, index) =>
    series.datasets.reduce((sum, dataset) => sum + (dataset.data[index] ?? 0), 0),
  );
}
